import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Modal, Spinner } from 'react-bootstrap';
import ccmService from '../../services/ccmService';
import './CCMConfiguration.css';

const CCMConfiguration = () => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [icdGroups, setIcdGroups] = useState([]);
  const [insurances, setInsurances] = useState([]);
  const [practiceId, setPracticeId] = useState('');
  const [showIcdModal, setShowIcdModal] = useState(false);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [newIcdGroup, setNewIcdGroup] = useState({ group_name: '', description: '', codes: [] });
  const [newInsurance, setNewInsurance] = useState({ insurance_name: '', insurance_type: 'Medicare', allow_self_pay: false });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pid = urlParams.get('practiceId') || '';
    setPracticeId(pid);
    
    if (pid) {
      fetchConfiguration(pid);
    } else {
      // If no practiceId, still show UI (empty state)
      setLoading(false);
    }
  }, []);

  const fetchConfiguration = async (pid) => {
    try {
      setLoading(true);
      const [configRes, icdRes, insRes] = await Promise.all([
        ccmService.getConfiguration(pid),
        ccmService.getIcdGroups(pid),
        ccmService.getEligibleInsurances(pid)
      ]);
      
      if (configRes.success) setConfig(configRes.data);
      if (icdRes.success) setIcdGroups(icdRes.data || []);
      if (insRes.success) setInsurances(insRes.data || []);
    } catch (error) {
      console.error('Error fetching configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfiguration = async () => {
    try {
      const response = await ccmService.saveConfiguration(practiceId, config);
      if (response.success) {
        alert('Configuration saved successfully');
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      alert('Failed to save configuration');
    }
  };

  const handleCreateIcdGroup = async () => {
    try {
      const response = await ccmService.createIcdGroup(practiceId, newIcdGroup);
      if (response.success) {
        setShowIcdModal(false);
        setNewIcdGroup({ group_name: '', description: '', codes: [] });
        fetchConfiguration(practiceId);
      }
    } catch (error) {
      console.error('Error creating ICD group:', error);
      alert('Failed to create ICD group');
    }
  };

  const handleAddInsurance = async () => {
    try {
      const response = await ccmService.addEligibleInsurance(practiceId, newInsurance);
      if (response.success) {
        setShowInsuranceModal(false);
        setNewInsurance({ insurance_name: '', insurance_type: 'Medicare', allow_self_pay: false });
        fetchConfiguration(practiceId);
      }
    } catch (error) {
      console.error('Error adding insurance:', error);
      alert('Failed to add insurance');
    }
  };

  if (loading) {
    return (
      <div className="ccm-cfg-container">
        <div className="ccm-cfg-loading">
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  return (
    <div className="ccm-cfg-container">
      <div className="ccm-cfg-page-title">
        <h3 className="ccm-cfg-title-text">CCM Configuration</h3>
      </div>

      {/* General Settings */}
      <Row className="mb-4">
        <Col>
          <div className="ccm-cfg-card">
            <div className="ccm-cfg-card-header">
              <h5 className="ccm-cfg-card-title">General Settings</h5>
            </div>
            <div className="ccm-cfg-card-body">
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Check
                      type="checkbox"
                      label="CCM Enabled"
                      checked={config?.ccm_enabled || false}
                      onChange={(e) => setConfig({ ...config, ccm_enabled: e.target.checked })}
                    />
                    <Form.Check
                      type="checkbox"
                      label="Auto Enrollment"
                      checked={config?.auto_enrollment || false}
                      onChange={(e) => setConfig({ ...config, auto_enrollment: e.target.checked })}
                      className="mt-2"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Billing Provider ID</Form.Label>
                      <Form.Control
                        type="number"
                        value={config?.billing_provider_id || ''}
                        onChange={(e) => setConfig({ ...config, billing_provider_id: e.target.value || null })}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <button className="ccm-cfg-btn ccm-cfg-btn-primary" onClick={handleSaveConfiguration}>
                  Save Configuration
                </button>
              </Form>
            </div>
          </div>
        </Col>
      </Row>

      {/* ICD Groups */}
      <Row className="mb-4">
        <Col>
          <div className="ccm-cfg-card">
            <div className="ccm-cfg-card-header">
              <h5 className="ccm-cfg-card-title">ICD Groups</h5>
              <button className="ccm-cfg-btn ccm-cfg-btn-sm" onClick={() => setShowIcdModal(true)}>
                Create ICD Group
              </button>
            </div>
            <div className="ccm-cfg-card-body">
              <div className="ccm-cfg-table-container">
                <div className="ccm-cfg-table-responsive">
                  <table className="ccm-cfg-table">
                <thead>
                  <tr>
                    <th>Group Name</th>
                    <th>Description</th>
                    <th>Codes Count</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {icdGroups.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="ccm-cfg-empty-cell">No ICD groups found</td>
                    </tr>
                  ) : (
                    icdGroups.map((group) => (
                      <tr key={group.id}>
                        <td>{group.group_name}</td>
                        <td>{group.description || 'N/A'}</td>
                        <td>{group.code_count || 0}</td>
                        <td>
                          <span className={`ccm-cfg-badge ${group.is_active ? 'ccm-cfg-badge-success' : 'ccm-cfg-badge-secondary'}`}>
                            {group.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
        </Col>
      </Row>

      {/* Eligible Insurances */}
      <Row>
        <Col>
          <div className="ccm-cfg-card">
            <div className="ccm-cfg-card-header">
              <h5 className="ccm-cfg-card-title">Eligible Insurances</h5>
              <button className="ccm-cfg-btn ccm-cfg-btn-sm" onClick={() => setShowInsuranceModal(true)}>
                Add Insurance
              </button>
            </div>
            <div className="ccm-cfg-card-body">
              <div className="ccm-cfg-table-container">
                <div className="ccm-cfg-table-responsive">
                  <table className="ccm-cfg-table">
                <thead>
                  <tr>
                    <th>Insurance Name</th>
                    <th>Type</th>
                    <th>Allow Self-Pay</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {insurances.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="ccm-cfg-empty-cell">No insurances found</td>
                    </tr>
                  ) : (
                    insurances.map((insurance) => (
                      <tr key={insurance.id}>
                        <td>{insurance.insurance_name}</td>
                        <td>{insurance.insurance_type}</td>
                        <td>{insurance.allow_self_pay ? 'Yes' : 'No'}</td>
                        <td>
                          <span className={`ccm-cfg-badge ${insurance.is_active ? 'ccm-cfg-badge-success' : 'ccm-cfg-badge-secondary'}`}>
                            {insurance.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
        </Col>
      </Row>

      {/* ICD Group Modal */}
      <Modal show={showIcdModal} onHide={() => setShowIcdModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create ICD Group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Group Name *</Form.Label>
              <Form.Control
                type="text"
                value={newIcdGroup.group_name}
                onChange={(e) => setNewIcdGroup({ ...newIcdGroup, group_name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newIcdGroup.description}
                onChange={(e) => setNewIcdGroup({ ...newIcdGroup, description: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>ICD Codes (comma-separated)</Form.Label>
              <Form.Control
                type="text"
                placeholder="E11.9, I10, M79.3"
                onChange={(e) => {
                  const codes = e.target.value.split(',').map(c => ({
                    icd_code: c.trim(),
                    icd_description: ''
                  }));
                  setNewIcdGroup({ ...newIcdGroup, codes });
                }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <div className="ccm-cfg-modal-footer">
          <button className="ccm-cfg-btn ccm-cfg-btn-secondary" onClick={() => setShowIcdModal(false)}>
            Cancel
          </button>
          <button className="ccm-cfg-btn ccm-cfg-btn-primary" onClick={handleCreateIcdGroup}>
            Create Group
          </button>
        </div>
      </Modal>

      {/* Insurance Modal */}
      <Modal show={showInsuranceModal} onHide={() => setShowInsuranceModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Eligible Insurance</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Insurance Name *</Form.Label>
              <Form.Control
                type="text"
                value={newInsurance.insurance_name}
                onChange={(e) => setNewInsurance({ ...newInsurance, insurance_name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Insurance Type *</Form.Label>
              <Form.Select
                value={newInsurance.insurance_type}
                onChange={(e) => setNewInsurance({ ...newInsurance, insurance_type: e.target.value })}
              >
                <option value="Medicare">Medicare</option>
                <option value="Medicaid">Medicaid</option>
                <option value="Commercial">Commercial</option>
                <option value="Self-Pay">Self-Pay</option>
              </Form.Select>
            </Form.Group>
            <Form.Check
              type="checkbox"
              label="Allow Self-Pay"
              checked={newInsurance.allow_self_pay}
              onChange={(e) => setNewInsurance({ ...newInsurance, allow_self_pay: e.target.checked })}
            />
          </Form>
        </Modal.Body>
        <div className="ccm-cfg-modal-footer">
          <button className="ccm-cfg-btn ccm-cfg-btn-secondary" onClick={() => setShowInsuranceModal(false)}>
            Cancel
          </button>
          <button className="ccm-cfg-btn ccm-cfg-btn-primary" onClick={handleAddInsurance}>
            Add Insurance
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default CCMConfiguration;

